<script setup>
// Lifts — the screen the app opens on.
//
// The job here is ONE number per lift: what are you working up to today. Every
// other decision (warm-up percentages, plates, collars) already has a home, so
// this screen refuses to grow one. Type the top set, tap Work up.
//
// Kit used to be a button in this bar. It is a tab now, so the bar carries
// nothing but the name.
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, onIonViewWillEnter } from '@ionic/vue';
import AppHeader from '../core/ui/AppHeader.vue';
import AppLargeTitle from '../core/ui/AppLargeTitle.vue';
import { lifts, saveLift } from '../store.js';
import { seedIfEmpty, adoptNewDefaults, currentKit, platesFor } from '../lib/seed.js';
import { loadBar, fmt, jumpRule, savedJump } from '../lib/loading.js';
import { rampFor, jumpFor, todayJump, todayScheme, setTodayJump, clearTodayJump } from '../lib/jump.js';
import { haptic } from '../core/util.js';

const router = useRouter();
const kit = ref(null);
const ready = ref(false);

// WHERE EACH RAMP GOT TO. The Ramp keeps the set you are on in session storage
// (`work-up:at:<id>`), so a row can say "In progress · set 4 of 7" and offer
// Resume rather than Work up. Storage is not reactive, and this page stays
// mounted underneath a pushed Ramp, so it is read again every time the page
// comes back into view.
const at = ref({});
function readPositions() {
  const next = {};
  for (const l of lifts.value) {
    try { next[l.id] = Number(sessionStorage.getItem(`work-up:at:${l.id}`)) || 0; } catch { next[l.id] = 0; }
  }
  at.value = next;
}
onIonViewWillEnter(readPositions);

onMounted(async () => {
  await seedIfEmpty();
  await adoptNewDefaults();
  kit.value = currentKit();
  readPositions();
  ready.value = true;
});

const unit = computed(() => kit.value?.unit ?? 'kg');
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// What a row needs to know, worked out once rather than three times in the
// template. `loadable` is the difference between a number and a plan: a top set
// the plates cannot make is not an error yet, but it cannot be worked up to.
const ORDER = ['squat', 'bench', 'deadlift', 'other'];
const ordered = computed(() => lifts.value.slice().sort(
  (a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind),
));

const rows = computed(() => ordered.value.map((lift) => {
  const top = Number(lift.topSet) || 0;
  const reps = Number(lift.topSetReps) || 1;
  const row = { lift, top, reps, loadable: false, problem: null, status: '', inProgress: false };
  if (!kit.value || !top) return row;
  const plates = platesFor(kit.value);
  const check = loadBar(top, kit.value, plates);
  if (!check.ok) {
    row.problem = check.reason === 'under-bar'
      ? `Lighter than the empty bar (${fmt(check.above)} ${unit.value}).`
      : `Your plates can't make ${fmt(top)} ${unit.value}.`;
    return row;
  }
  // Today's warm-ups and jump when they differ, so the count is the Ramp's.
  const ramp = rampFor(lift, kit.value, plates);
  const sets = ramp.ok ? ramp.sets : [];
  const pos = at.value[lift.id] ?? 0;
  row.loadable = true;
  row.jump = jumpFor(lift, unit.value);
  row.saved = savedJump(lift, unit.value);
  row.today = todayJump(lift, unit.value) != null;
  row.inProgress = pos > 0 && pos < sets.length;
  // In words, never in colour: where you are, or what the ramp is.
  if (row.inProgress) row.status = `In progress · set ${pos + 1} of ${sets.length}`;
  else if (sets.length && pos >= sets.length) row.status = `Lift done · ${plural(sets.length, 'set', 'sets')}`;
  else if (sets.length) row.status = `${plural(sets.length, 'set', 'sets')} · ${fmt(sets[0].total)} → ${fmt(top)}`;
  return row;
}));

// Stored on blur rather than on every keystroke: a half-typed "1" in a field on
// the way to "180" is a real value the ramp would rebuild itself around.
function commit(lift, raw) {
  const n = raw === '' || raw == null ? null : Number(raw);
  const next = Number.isFinite(n) && n > 0 ? n : null;
  if (next === (lift.topSet ?? null)) return;
  saveLift({ ...lift, topSet: next });
}

// THE FIELD IS AS WIDE AS ITS NUMBER, so the dashed underline sits under the
// digits the way the design draws it, and "kg × 1" stays beside them. A hidden
// copy of the text sizes the box (see `.fit` below) — `ch` is the width of a
// zero, and this serif's 1 and 8 are much narrower than its 0.
//
// Updated on the ELEMENT while typing, never through reactive state: Vue
// re-patches an input's `value` on every render of its row, so a keystroke
// that re-rendered the row put the stored number back over what was typed.
const fit = (el) => { el.parentElement.dataset.value = el.value || el.placeholder; };

// TODAY'S LAST JUMP, folded into the status line. The usual one is Kit's; this
// is for the day it isn't right, so it stays one line until asked for and never
// writes the lift record — tomorrow opens on the usual jump again.
const open = ref(null);
const rule = computed(() => jumpRule(unit.value));
const toggleJump = (id) => { open.value = open.value === id ? null : id; };
function nudge(r, by) {
  setTodayJump(r.lift, r.jump + by * rule.value.step, unit.value);
  haptic('selection');
}

const workUp = (lift) => router.push(`/tabs/lifts/${lift.id}/ramp`);
const goScheme = (lift) => router.push(`/tabs/lifts/${lift.id}/scheme`);
</script>

<template>
  <IonPage class="work-up lifts-page">
    <AppHeader title="Lifts" />

    <IonContent :fullscreen="true">
      <AppLargeTitle title="Lifts" />

      <p v-if="ready && !rows.length" class="note">
        No top sets yet. Put in today's squat and the ramp builds itself.
      </p>

      <ul v-else class="lifts">
        <li v-for="r in rows" :key="r.lift.id" class="lift">
          <div class="head">
            <h2 class="name">{{ r.lift.name }}</h2>
            <label class="entry">
              <span class="vh">Top set for {{ r.lift.name }}, in {{ unit }}</span>
              <span class="fit" :data-value="r.lift.topSet || '—'">
                <input
                  class="top"
                  type="text"
                  inputmode="decimal"
                  autocomplete="off"
                  size="1"
                  :value="r.lift.topSet || ''"
                  placeholder="—"
                  @input="fit($event.target)"
                  @change="commit(r.lift, $event.target.value)"
                  @blur="commit(r.lift, $event.target.value)"
                >
              </span>
              <span class="per" aria-hidden="true">{{ unit }} × {{ r.reps }}</span>
            </label>
          </div>

          <div class="summary">
            <p class="sub">
              <template v-if="r.problem">{{ r.problem }}</template>
              <template v-else-if="r.loadable">{{ r.status }}</template>
              <template v-else>Set a top set</template>
            </p>
            <!-- Today's jump is said in WORDS and in SHAPE: "today", and an
                 outlined pill where the usual one is only underlined. -->
            <button
              v-if="r.loadable"
              type="button"
              class="jump"
              :class="{ today: r.today }"
              :aria-expanded="open === r.lift.id"
              :aria-controls="`jump-${r.lift.id}`"
              @click="toggleJump(r.lift.id)"
            >
              Last jump {{ fmt(r.jump) }} {{ unit }}<template v-if="r.today"> · today</template>
            </button>
          </div>

          <div v-if="r.loadable && open === r.lift.id" :id="`jump-${r.lift.id}`" class="jump-panel">
            <div class="stepper" role="group" :aria-label="`Last jump for ${r.lift.name}, today only`">
              <button
                type="button" class="step" :disabled="r.jump <= rule.step"
                :aria-label="`${fmt(rule.step)} ${unit} less`" @click="nudge(r, -1)"
              >−</button>
              <span class="value" aria-live="polite">{{ fmt(r.jump) }}<span class="value-unit">{{ unit }}</span></span>
              <button
                type="button" class="step" :disabled="r.jump >= rule.max"
                :aria-label="`${fmt(rule.step)} ${unit} more`" @click="nudge(r, 1)"
              >+</button>
            </div>
            <button v-if="r.today" type="button" class="wu-link reset" @click="clearTodayJump(r.lift)">
              Back to {{ fmt(r.saved) }} {{ unit }}
            </button>
            <span v-else class="hint">Today only.</span>
          </div>

          <div class="actions">
            <button type="button" class="wu-btn wu-btn--row go" :disabled="!r.loadable" @click="workUp(r.lift)">
              {{ r.inProgress ? 'Resume' : 'Work up' }}
            </button>
            <!-- Today's warm-ups, said the way today's jump is: in words. -->
            <button type="button" class="wu-btn-quiet wu-btn--row" @click="goScheme(r.lift)">
              Warm-ups<template v-if="todayScheme(r.lift)"> · today</template>
            </button>
          </div>
        </li>
      </ul>
    </IonContent>
  </IonPage>
</template>

<style scoped>
/* Values are screens.py `lifts()`'s. */
.note {
  margin: 0; padding: 24px var(--wu-pad); max-width: 36ch;
  font: 400 0.875rem/1.6 var(--face-mono); color: var(--dim);
}
.lifts { margin: 0; padding: 12px 0 0; list-style: none; }
.lift {
  display: flex; flex-direction: column; gap: 10px;
  padding: 20px var(--wu-pad) 22px;
  border-bottom: 1px solid var(--line);
}

.head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.name {
  margin: 0; min-width: 0;
  font: 400 1.75rem/1 var(--face-serif); letter-spacing: 0; color: var(--ink);
}

/* The entry field IS the screen: the number in the serif, a dashed underline
   that says "this is yours to change", sized like the answer rather than a
   form control, because it is read from the floor at arm's length. */
.entry { display: flex; align-items: baseline; gap: 6px; flex: none; }
/* The input and a hidden copy of its text share one grid cell, so the cell —
   and the input stretched across it — is exactly as wide as the number. */
.fit { display: inline-grid; }
.fit::after {
  content: attr(data-value);
  visibility: hidden; white-space: pre; height: 0; overflow: hidden;
  padding: 0 2px;
  font: 400 2.5rem/1 var(--face-serif); letter-spacing: 0;
}
.fit::after, .fit .top { grid-area: 1 / 1; }
/* A HEIGHT, not just line-height: Chrome sizes a text input's line box from
   the font's own ascent and descent (1.3em for this serif), so `line-height: 1`
   left it 55px tall and every row 12px taller than the design. 40px of type,
   2px under it, the 1.5px dash. */
.top {
  box-sizing: border-box; height: 43.5px;
  width: 100%; min-width: 0;
  margin: 0; padding: 0 2px 2px;
  border: 0; border-bottom: 1.5px dashed var(--faint); border-radius: 0;
  background: transparent; color: var(--ink);
  font: 400 2.5rem/1 var(--face-serif); letter-spacing: 0;
  text-align: right;
  -webkit-appearance: none; appearance: none;
}
.top::placeholder { color: var(--faint); }
.top:focus { outline: none; border-bottom: 1.5px solid var(--ink); }
.per { font: 400 0.8125rem/1 var(--face-mono); color: var(--dim); white-space: nowrap; }

/* The status and today's jump share a line, and the jump wraps under it
   rather than squeezing the status at 200% text. */
.summary {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
  gap: 0 12px; min-height: 36px;
}
.sub { flex: 1 1 auto; margin: 0; font: 400 0.75rem/1.4 var(--face-mono); color: var(--dim); }

/* A link-weight control with a full-size target: the usual jump is a fact to
   read, not a third button to press. Drawn 36px as the design has it; the
   extra 8px of target is invisible, above and below. Today's is an outlined
   pill, so it reads as changed without a colour being spent on it. */
.jump {
  position: relative;
  min-height: 36px; margin: 0; padding: 0;
  border: 1px solid transparent; border-radius: 12px; background: none;
  color: var(--dim); font: 400 0.75rem/1 var(--face-mono); white-space: nowrap;
  text-decoration: underline; text-decoration-color: var(--faint);
  text-decoration-thickness: 1px; text-underline-offset: 4px;
  cursor: pointer;
}
.jump::after { content: ''; position: absolute; inset: -4px 0; }
.jump.today { padding: 0 12px; border-color: var(--field-line); color: var(--ink); text-decoration: none; }

.jump-panel { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; }
.stepper { display: flex; align-items: center; gap: 6px; }
.step {
  width: 44px; height: 44px; margin: 0; padding: 0;
  border: 1px solid var(--field-line); border-radius: 12px; background: var(--field);
  color: var(--ink); font: 400 1.25rem/1 var(--face-mono); cursor: pointer;
}
.step:disabled { border-style: dashed; border-color: var(--faint); background: transparent; color: var(--faint); cursor: default; }
.value {
  min-width: 5ch; padding: 0 4px; text-align: center;
  font: 500 0.9375rem/1 var(--face-mono); font-variant-numeric: tabular-nums; color: var(--ink);
}
.value-unit { margin-left: 3px; font-weight: 400; color: var(--dim); }
.reset { font-size: 0.75rem; }
.hint { font: 400 0.75rem/1.3 var(--face-mono); color: var(--dim); }

.actions { display: flex; gap: 10px; }
.actions .go { flex: 1 1 auto; }

.vh { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
</style>
